import { PlatformBridge } from './bridge';

export interface LevelInfo {
  value?: number;
  max?: number;
}

export interface SupplyLevels {
  black?: LevelInfo;
  cyan?: LevelInfo;
  magenta?: LevelInfo;
  yellow?: LevelInfo;
}

export interface PrintCounters {
  total?: number;
  color?: number;
  mono?: number;
}

export interface PrinterConfig {
  duplex?: string;
  duplexBinding?: string;
  resolution?: string;
}

export interface PrinterStatus {
  // System info
  name?: string;
  description?: string;
  serialNumber?: string;
  uptime?: string;

  // Toner (%)
  toner?: SupplyLevels;

  // Drum (pages remaining)
  drum?: SupplyLevels;

  // Belt and Fuser (pages remaining)
  belt?: LevelInfo;
  fuser?: LevelInfo;

  // Print counters (OKI proprietary)
  counters?: PrintCounters;

  // Configuration
  config?: PrinterConfig;
}

export interface PrinterInfo {
  ip: string;
  port?: number;
  description?: string;
  model?: string;
  printerStatus?: PrinterStatus;
}

export interface PrinterBridge {
  findPrinter(params: { port?: number }): Promise<{
    status: 'found' | 'not_found';
    printers: PrinterInfo[];
  }>;
  testConnection(params: {
    host: string;
    port: number;
  }): Promise<{ status: 'connected' | 'disconnected' }>;
  print(params: {
    host: string;
    port: number;
    base64Data: string;
  }): Promise<{ status: 'printed' | 'error' }>;
  getPrinterStatus(params: { host: string; port?: number; timeout?: number }): Promise<{
    status: 'success' | 'error';
    printerStatus?: PrinterStatus;
    error?: string;
  }>;
}

function getCapacitorPrinter(): PrinterBridge | null {
  const plugins = PlatformBridge.capacitorPlugins;
  if (!plugins) return null;
  return (plugins.Printer as PrinterBridge) ?? null;
}

export const PrintBridge = {
  getPrinter(): PrinterBridge | null {
    return getCapacitorPrinter();
  },

  async getPrinterStatus(params: { host: string; port?: number; timeout?: number }): Promise<{
    status: 'success' | 'error';
    printerStatus?: PrinterStatus;
    error?: string;
  }> {
    const printer = PrintBridge.getPrinter();
    if (!printer) {
      return {
        status: 'error',
        error: 'Printer bridge not available',
      };
    }

    try {
      return await printer.getPrinterStatus(params);
    } catch (error) {
      return {
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },
};
